import { Box } from '@apia/theme';
import { TKeyHandlerProps } from '../store/selection/types';
import { handleMouseDownEvent } from './handleMouseDownEvent';
import { handleKeyDownEvent } from './handleKeyDownEvent';
import {
  responsiveTableActions,
  responsiveTableStore,
  useResponsiveTable,
} from '../store';
import { TResponsiveTableState } from '../store/types';
import { shallowEqual } from '@apia/store';
import { FC, useRef } from 'react';
import { applyFocusAttributes } from './applyFocusAttributes';
import { EventEmitter, noNaN } from '@apia/util';

/**
 * Este controlador maneja la selección en una forma más directa que el anterior, manipulando los elementos del DOM directamente, y persistiendo en el store estas propiedades para garantizar la consistencia en los renderizados posteriores.
 *
 * Esto permite una experiencia más fluida en el manejo del foco. Además, al no depender del estado para calcular las propiedades, se tiene un mayor control sobre qué elementos son afectados y de qué manera, permitiendo
 */
export class Controller2 {
  constructor(public readonly tableName: string) {
    this.handleClickEvent = handleMouseDownEvent.bind(this);
    this.handleKeyDownEvent = handleKeyDownEvent.bind(this);
  }

  events = new EventEmitter<{
    onRowClick: Parameters<NonNullable<TKeyHandlerProps['onRowClick']>>;
    onSelectRows: Parameters<NonNullable<TKeyHandlerProps['onSelectRows']>>;
    onChangeSelection: Parameters<
      NonNullable<TKeyHandlerProps['onChangeSelection']>
    >;
  }>();

  #unsubscribe = () => {};
  setResponsiveTableContextEvents(
    evs: Pick<
      TKeyHandlerProps,
      'onRowClick' | 'onSelectRows' | 'onChangeSelection'
    >,
  ) {
    this.#unsubscribe();
    const u1 = this.events.on('onChangeSelection', (a) =>
      evs.onChangeSelection?.(...a),
    );
    const u2 = this.events.on('onRowClick', (a) => evs.onRowClick?.(...a));
    const u3 = this.events.on('onSelectRows', (a) => evs.onSelectRows?.(...a));

    this.#unsubscribe = () => {
      u1();
      u2();
      u3();
    };
  }

  private unsubscribe = () => {};
  private el: HTMLElement | null = null;
  setRef = (el: HTMLElement) => {
    this.unsubscribe();
    this.el = el;
    if (el) {
      el.addEventListener('keydown', this.handleKeyDownEvent);
      el.addEventListener('mousedown', this.handleClickEvent);
      el.addEventListener('dblclick', this.handleDoubleClickEvent);

      this.unsubscribe = () => {
        el.removeEventListener('keydown', this.handleKeyDownEvent);
        el.removeEventListener('mousedown', this.handleClickEvent);
        el.removeEventListener('dblclick', this.handleDoubleClickEvent);
      };
    }
  };

  handleClickEvent: (ev: MouseEvent) => void;
  handleKeyDownEvent: (ev: KeyboardEvent) => void;
  handleDoubleClickEvent = (ev: MouseEvent) => {
    const target = ev.target;

    if (target instanceof HTMLElement) {
      const row = target.closest('tr');
      const table = target.closest('table');

      const selectedRows = this.getSelection().reduce<
        { index: number; row: any }[]
      >((acc, index) => {
        if (index >= 0) {
          acc.push({
            index,
            row: this.getState().rows[index],
          });
        }
        return acc;
      }, []);

      const rowIndex = noNaN(row?.dataset.rowindex || 'a', -1);

      if (row && table && selectedRows.length > 0 && rowIndex >= 0) {
        this.events.emit('onSelectRows', [
          selectedRows,
          this.getState().focusedRow as number,
        ]);
      }
    }
  };

  /**
   * Returns a copy of the current state object
   */
  getState() {
    return {
      ...responsiveTableStore.getState().responsiveTableSlice[this.tableName],
    };
  }
  getSelection() {
    const selectedRows = new Set(this.getState().selectedRows);
    return [...selectedRows.values()];
  }
  setState(state: Partial<TResponsiveTableState>) {
    if (
      state.selectedRows &&
      !shallowEqual(state.selectedRows, this.getState().selectedRows)
    ) {
      this.events.emit('onChangeSelection', [
        state.selectedRows.map((index) => ({
          index,
          row: this.getState().rows[index],
        })),
      ]);
    }

    if (
      state.scrollIntoViewRow !== undefined &&
      state.scrollIntoViewRow !== this.getState().scrollIntoViewRow
    ) {
      const row = this.el?.querySelector<HTMLElement>(
        `tr[data-rowindex="${state.scrollIntoViewRow}"]`,
      );
      if (row) {
        row.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }

    responsiveTableStore.dispatch(
      responsiveTableActions.controller2UpdateSelectionState({
        tableName: this.tableName,
        ...state,
      }),
    );
  }

  updateSelectionState() {
    window.requestAnimationFrame(() => {
      if (this.el) {
        const table = this.el.querySelector<HTMLElement>('table');
        if (table) {
          applyFocusAttributes(table, this.getState(), this.getState());
        }
      }
    });
  }
}

const Controller2Component = ({
  controller,
  ...props
}: TKeyHandlerProps & { controller: Controller2 }) => {
  controller.setResponsiveTableContextEvents({
    onChangeSelection: props.onChangeSelection,
    onSelectRows: props.onSelectRows,
    onRowClick: props.onRowClick,
  });

  const previousColumns = useRef<any[]>([]);
  const previousRows = useRef<any[]>([]);
  const box = useRef<HTMLElement | null>(null);

  useResponsiveTable((state) => {
    if (
      !Object.is(
        state.responsiveTableSlice[controller.tableName]?.columns,
        previousColumns.current,
      )
    ) {
      previousColumns.current =
        state.responsiveTableSlice[controller.tableName]?.columns;
      controller.updateSelectionState();
    }
    if (
      !Object.is(
        state.responsiveTableSlice[controller.tableName]?.rows,
        previousRows.current,
      )
    ) {
      previousRows.current =
        state.responsiveTableSlice[controller.tableName]?.rows;
      controller.updateSelectionState();
    }
  });

  return (
    <Box
      {...props}
      ref={(el) => {
        controller.setRef(el);
        box.current = el;
      }}
    />
  );
};

export function makeController2(
  tableName: string,
): [Controller2, FC<TKeyHandlerProps>] {
  const controller = new Controller2(tableName);

  return [
    controller,
    (props: TKeyHandlerProps) => {
      return <Controller2Component {...props} controller={controller} />;
    },
  ];
}
